/* global previews */
import { TEXT_PREFIX_ID } from '@frontend/constants/templates-field-prefixes';
import {
  getLockValue,
  getPositioningOption,
  getShadowFabricInstance,
  isInGroup,
  updateBoundingBoxInfo
} from '@frontend/utils';
import parseText from '@/common/preview/parse-text';
import { scaleControlsCornerSize } from '@frontend/constants/fabric-constants';
import { formDataToObject } from '@frontend/lib/utils/form-data-to-object';
import { ShowPageScopeCollector } from '@frontend/group/modules/scope-collectors/show-page-collector';
import { fabric } from 'fabric';
import {getGroupNameByAddFieldId, getSmartObjectsIds} from "@/common/preview/draw-smart-object";
import { FIELD_TYPE_SMART_OBJECT } from "@frontend/constants/type-fields-of-template";
import { deleteTextLayer } from "@/common/preview/common-helper";

const getScopePage = () => ShowPageScopeCollector.get(['countrySelect', 'languageSelect', 'canvas_data', 'updateLayoutChanges']);

export async function drawText({
  fields,
  positioning_options,
  data,
  canvas,
  shadows,
  id,
  group_fields,
  dimension,
  previewBoundingBoxData
}) {
  canvas.getObjects().forEach(function (o) {
    if (o.id.includes(TEXT_PREFIX_ID)) {
      canvas.remove(o);
    }
  });

  const { canvas_data, updateLayoutChanges } = getScopePage();
  
  canvas_data[id].textCoords = [];
  let index = 0;
  let i = 0;

  /* used to save bounding box info */
  const formData = new FormData();
  formData.append('type', 'Text');
  
  const filteredFields = fields.filter(field => !!data[field.element_id]);
  for (const field of filteredFields) {
    const options = JSON.parse(field.options);
    const groupName = getGroupNameByAddFieldId(
      String(field.id),
      canvas_data[id]
    );

    if (
      (field.type === 'Text' || field.type === 'Text Options') &&
      (!group_fields || isInGroup(group_fields, field.name))
    ) {
      const positioningOption = getPositioningOption(
        positioning_options,
        data,
        fields,
        field
      );

      let text_val = data[field.element_id];
      if (data.product_texts) {
        const { country, language } = getScopePage();
        const product_texts = JSON.parse(data.product_texts);
        const text_data = product_texts[country + '_' + language];
        
        if (text_data) {
          text_val = text_data.field_texts[i];
        }
      }

      const color = data[`${field.element_id}_color`];
      const font = getDrawFont(options['Font Selector'], data[`${field.element_id}_font`], canvas);
      const font_size = data[`${field.element_id}_fontsize`];
      const angle = data[`${field.element_id}_angle`];
      const opacity = data[`${field.element_id}_opacity`];
      const alignment = data[`${field.element_id}_alignment`];
      const isShadow = +data[`${field.element_id}_shadow`];
      const visible = data[`${field.element_id}_visible`] ?? true;
      let offset_x = data[`${field.element_id}_offset_x`];
      let offset_y = data[`${field.element_id}_offset_y`];
      let width = +data[`${field.element_id}_width`] || +options['Width'];
      let x = parseFloat(options['X']) + dimension[4].x;
      let y = parseFloat(options['Y']) + dimension[4].y;
      let tracking = data[`${field.element_id}_tracking`];
      tracking = tracking ? tracking : (options['Text Tracking'] ? parseFloat(options['Text Tracking']) : 0);
      const isCurved = data[`${field.element_id}_is_curved`] || false;
      const maxChars = Number(data[`${field.element_id}_max_chars`] || parseInt(options['Max Chars']) || 10000);
      const curvedDiameter = Number(data[`${field.element_id}_diameter`]) || 1000;
      const curvedKerning = 1.5;

      if (positioningOption) {
        x = positioningOption.x == null ? x : positioningOption.x;
        y = positioningOption.y == null ? y : positioningOption.y;
        width = positioningOption.width == null ? width : positioningOption.width;
      }

      if (text_val) {
        const { text, styles } = parseText(text_val, color ? color : options['Font Color']);
        const textBox = new fabric.Textbox(text, {
          id: TEXT_PREFIX_ID + index,
          groupName: groupName,
          element_id: field.element_id,
          order: parseInt(options['Order']),
          top: y + parseFloat(offset_y),
          left: getLeft(canvas_data[id], field, x, offset_x),
          width: width,
          opacity,
          fixedWidth: options['Size To Fit'] ? width : 0,
          originX: canvas_data[id].spacingFieldPosition[field.name] ? 'left' : 'left',
          originY: canvas_data[id].spacingFieldPosition[field.name] ? 'top' : 'top',
          textAlign: alignment ? alignment : options['Alignment'] ? options['Alignment'] : 'left',
          fontSize: font_size ? font_size : parseInt(options['Font Size']),
          fontColor: color ? color : options['Font Color'],
          lineHeight: options['Leading'] ? parseFloat(options['Leading']) : 1,
          fontFamily: font ? font : options['Font'] ? options['Font'] : 'Proxima-Nova-Semibold',
          charSpacing: +tracking,
          styles: styles,
          fontSelector: options['Font Selector'] === 'Yes',
          colorSelector: options['Color Selector'] === 'Yes',
          fontThemeSelector: options['Font Selector'] === 'Theme',
          _wordJoiners: /.^/,
          fieldId: field.id,
          templateField: canvas_data[id].templateInstance.getFieldById(field.id),
          angle: angle ? parseFloat(angle) : 0,
          visible,
          isCurved,
          curvedDiameter,
          curvedKerning,
          maxChars,
          updateLayoutChanges,
          deleteLayer: deleteTextLayer
        });
        
        textBox.set({
          shadow: getShadowFabricInstance(id, textBox.element_id, shadows, isShadow),
          locked: getLockValue(id, textBox.element_id),
        });
        
        const pointLeftTop = textBox.getPointByOrigin('left', 'top');
        const lockMoveAfterChanged = function (e) {
          this.setPositionByOrigin(pointLeftTop, 'left', 'top');
        };

        if (options['Moveable'] !== 'Yes') {
          textBox.on('moving', lockMoveAfterChanged);
        }

        let group_x = 0;
        let group_y = 0;
        if (groupName && canvas_data[id].smartObjCoords[groupName]) {
          group_x = parseInt(canvas_data[id].smartObjCoords[groupName]['X']);
          group_y = parseInt(canvas_data[id].smartObjCoords[groupName]['Y']);
        }
        
        textBox.set({ left: textBox.left + group_x - dimension[2] });
        textBox.set({ top: textBox.top + group_y - dimension[3] });

        let bound = textBox.getBoundingRect();

        if (!canvas_data[id].spacingFieldPosition[field.name]) {
          if (offset_x === 0 && offset_y === 0) {
            textBox.set({ left: textBox.left + bound.width / 2 });
            textBox.set({ top: textBox.top + bound.height / 2 });

            offset_x = bound.width / 2;
            offset_y = bound.height / 2;
          }
        }
        
        scaleControlsCornerSize(textBox);
        canvas.add(textBox);

        /* update bounding box info */
        bound = textBox.getBoundingRect();

        formData.append('element_ids[]', field.element_id);
        formData.append(field.element_id + '_width', textBox.width);
        formData.append(field.element_id + '_height', textBox.height);
        formData.append(field.element_id + '_b_width', bound.width);
        formData.append(field.element_id + '_b_height', bound.height);
        formData.append(field.element_id + '_offset_x', offset_x);
        formData.append(field.element_id + '_offset_y', offset_y);

        const overflow_width = textBox.width > width ? (textBox.width - width) / 2 : 0;
        
        textBox.set({ left: textBox.left - overflow_width });

        index++;
        canvas_data[id].textCoords.push({
          x: x + group_x,
          y: y + group_y
        });
      }
      
      i++;
    }
  }

  /* UPDATE BOUNDING BOX INFO */
  if (previewBoundingBoxData) {
    previewBoundingBoxData.data.push(formDataToObject(formData));
  } else {
    updateBoundingBoxInfo(formData, id);
  }

  canvas.requestRenderAll();
}

const getDrawFont = (fontOption, elementFont, canvas) => {
  const canvasParams = previews.find((x) => x.id === canvas.instance_id);

  if (fontOption === 'Yes') {
    return elementFont;
  } else if (fontOption === 'Theme') {
    const checkFont = canvasParams.themeFonts.some((cp) => cp === elementFont);

    if (canvasParams.themeFonts.length && !checkFont) {
      return canvasParams.themeFonts[0];
    }

    return elementFont;
  }

  return false;
};

const getLeft = (canvas_data, field, x, offset_x) => (
  canvas_data.spacingFieldPosition[field.name]
    ? canvas_data.spacingFieldPosition[field.name].x
    : x + parseFloat(offset_x)
);
